import { useCallback } from 'react';

import { Result, err } from 'neverthrow';

import { LoadBuildingGuardrailCustomer } from 'contexts/loadBuildingGuardrail';
import { useLoadBuildingGuardrail } from 'hooks/useLoadBuildingGuardrail';
import {
  CreateLoadRequest,
  CreateLoadResponse,
  createLoad,
} from 'lib/api/createLoad';
import {
  GUARDED_CREATE_LOAD_ERROR_MESSAGE,
  resolveGuardrailCustomer,
  shouldAbortGuardedCreateLoad,
  withProdTMSOverride,
} from 'lib/loadBuilding/guardrail';
import { Customer, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';

/**
 * Wraps createLoad with prod-TMS guardrail dialog / override handling.
 */
export function useGuardedCreateLoad() {
  const { requestSubmit } = useLoadBuildingGuardrail();

  return useCallback(
    async (
      customer:
        Customer | TMSCustomer | LoadBuildingGuardrailCustomer | undefined,
      reqData: CreateLoadRequest,
      tmsCustomers?: Maybe<TMSCustomer[]>
    ): Promise<Result<CreateLoadResponse, ApiError> | null> => {
      let result: Result<CreateLoadResponse, ApiError> | undefined;
      try {
        await requestSubmit(
          resolveGuardrailCustomer(customer, tmsCustomers),
          async (opts) => {
            result = await createLoad(withProdTMSOverride(reqData, opts));
          }
        );
      } catch (error) {
        if (shouldAbortGuardedCreateLoad(error)) {
          return null;
        }
        return err({ message: GUARDED_CREATE_LOAD_ERROR_MESSAGE });
      }
      return result ?? null;
    },
    [requestSubmit]
  );
}
